/**
 * TextStyleManager.ts;
 * Gestionnaire des styles de texte nomm\u00e9s;
 *
 * Fonctionnalit\u00e9s :
 * - Cr\u00e9er, modifier, supprimer des styles nomm\u00e9s;
 * - Appliquer des styles aux TextNodes;
 * - Sauvegarder/charger depuis localStorage;
 * - Styles par d\u00e9faut (Heading 1, Heading 2, Body, etc.)
 */
import Konva from 'konva';
import { logger } from '../utils/ProfessionalLogger';
import { TextStyle } from '../types';
import { useAppStore } from '../services/StateService';
/**
 * Gestionnaire des styles de texte nomm\u00e9s;
 */
export class TextStyleManager {
  private static STORAGE_KEY = 'snapbooth_text_styles';
  // Styles par d\u00e9faut;
  private static DEFAULT_STYLES: TextStyle[] = [
    {
      id: 'heading-1',
      name: 'Titre 1',
      fontFamily: 'Arial',
      fontSize: 48,
      fontWeight: 700,
      color: '#000000',
      lineHeight: 1.2,
      letterSpacing: 0,
    },
    {
      id: 'heading-2',
      name: 'Titre 2',
      fontFamily: 'Arial',
      fontSize: 36,
      fontWeight: 600,
      color: '#000000',
      lineHeight: 1.3,
      letterSpacing: 0,
    },
    {
      id: 'heading-3',
      name: 'Titre 3',
      fontFamily: 'Arial',
      fontSize: 24,
      fontWeight: 600,
      color: '#000000',
      lineHeight: 1.4,
      letterSpacing: 0,
    },
    {
      id: 'body',
      name: 'Corps de texte',
      fontFamily: 'Arial',
      fontSize: 16,
      fontWeight: 400,
      color: '#000000',
      lineHeight: 1.5,
      letterSpacing: 0,
    },
    {
      id: 'caption',
      name: 'L\u00e9gende',
      fontFamily: 'Arial',
      fontSize: 12,
      fontWeight: 400,
      color: '#666666',
      lineHeight: 1.4,
      letterSpacing: 0,
    },
  ];
  constructor() {
    this.initialize();
  }
  /**
   * Initialise le gestionnaire;
   */
  public initialize(): void {
    // Charger les styles sauvegard\u00e9s ou cr\u00e9er les styles par d\u00e9faut;
    const savedStyles = this.loadFromStorage();
    if (savedStyles.length === 0) {
      this.initializeDefaultStyles();
    } else {
      this.syncWithState(savedStyles);
    }
    logger.info('\u2705 TextStyleManager initialis\u00e9');
  }
  /**
   * Initialise les styles par d\u00e9faut;
   */
  private initializeDefaultStyles(): void {
    const store = useAppStore.getState();
    TextStyleManager.DEFAULT_STYLES.forEach(style => {
      store.addTextStyle(style);
    });
    this.saveToStorage(TextStyleManager.DEFAULT_STYLES);
    logger.info('\ud83d\udcdd Styles par d\u00e9faut cr\u00e9\u00e9s');
  }
  /**
   * Synchronise avec l'\u00e9tat global;
   */
  private syncWithState(styles: TextStyle[]): void {
    const store = useAppStore.getState();
    // Remplacer tous les styles dans le state;
    styles.forEach(style => {
      store.addTextStyle(style);
    });
  }
  /**
   * Cr\u00e9e un nouveau style;
   */
  public createStyle(style: Omit<TextStyle, 'id'>): TextStyle {
    const newStyle: TextStyle = {
      ...style,
      id: this.generateStyleId(style.name),
    };
    const store = useAppStore.getState();
    store.addTextStyle(newStyle);
    this.saveToStorage(this.getAllStyles());
    logger.info(`\u2705 Style cr\u00e9\u00e9: ${newStyle.name}`);
    return newStyle;
  }
  /**
   * Met \u00e0 jour un style existant;
   */
  public updateStyle(styleId: string, updates: Partial<TextStyle>): void {
    const store = useAppStore.getState();
    store.updateTextStyle(styleId, updates);
    this.saveToStorage(this.getAllStyles());
    logger.info(`\u2705 Style mis \u00e0 jour: ${styleId}`);
  }
  /**
   * Supprime un style;
   */
  public deleteStyle(styleId: string): void {
    const store = useAppStore.getState();
    store.deleteTextStyle(styleId);
    this.saveToStorage(this.getAllStyles());
    logger.info(`\ud83d\uddd1\ufe0f Style supprim\u00e9: ${styleId}`);
  }
  /**
   * R\u00e9cup\u00e8re un style par son ID;
   */
  public getStyle(styleId: string): TextStyle | undefined {
    const store = useAppStore.getState();
    return store.textTool.savedStyles.find(s => s.id === styleId);
  }
  /**
   * R\u00e9cup\u00e8re tous les styles;
   */
  public getAllStyles(): TextStyle[] {
    const store = useAppStore.getState();
    return store.textTool.savedStyles;
  }
  /**
   * Duplique un style;
   */
  public duplicateStyle(styleId: string): TextStyle | null {
    const originalStyle = this.getStyle(styleId);
    if (!originalStyle) {
      logger.warn(`\u26a0\ufe0f Style introuvable: ${styleId}`);
      return null;
    }
    const duplicatedStyle = this.createStyle({
      name: `${originalStyle.name} (copie)`,
      fontFamily: originalStyle.fontFamily,
      fontSize: originalStyle.fontSize,
      fontWeight: originalStyle.fontWeight,
      color: originalStyle.color,
      lineHeight: originalStyle.lineHeight,
      letterSpacing: originalStyle.letterSpacing,
    });
    logger.info(`\ud83d\udccb Style dupliqu\u00e9: ${originalStyle.name}`);
    return duplicatedStyle;
  }
  /**
   * Applique un style \u00e0 un TextNode Konva;
   */
  public applyStyleToNode(node: Konva.Text, styleId: string): void {
    const style = this.getStyle(styleId);
    if (!style) {
      logger.warn(`\u26a0\ufe0f Style introuvable: ${styleId}`);
      return;
    }
    // Appliquer toutes les propri\u00e9t\u00e9s du style;
    node.fontFamily(style.fontFamily);
    node.fontSize(style.fontSize);
    node.fontStyle(this.getFontStyleString(style.fontWeight));
    node.fill(style.color);
    if (style.lineHeight) {
      node.lineHeight(style.lineHeight);
    }
    if (style.letterSpacing) {
      node.letterSpacing(style.letterSpacing);
    }
    // Marquer le node avec le styleId pour r\u00e9f\u00e9rence future;
    node.setAttr('styleId', styleId);
    logger.info(`\ud83c\udfa8 Style appliqu\u00e9: ${style.name}`);
  }
  /**
   * Cr\u00e9e un style \u00e0 partir d'un TextNode existant;
   */
  public createStyleFromNode(node: Konva.Text, name: string): TextStyle {
    const newStyle = this.createStyle({
      name,
      fontFamily: node.fontFamily(),
      fontSize: node.fontSize(),
      fontWeight: this.getFontWeightFromString(node.fontStyle()),
      color: typeof node.fill() === 'string' ? (node.fill() as string) : '#000000',
      lineHeight: node.lineHeight(),
      letterSpacing: node.letterSpacing() || 0,
    });
    logger.info(`\u2728 Style cr\u00e9\u00e9 depuis node: ${name}`);
    return newStyle;
  }
  /**
   * R\u00e9initialise aux styles par d\u00e9faut;
   */
  public resetToDefaults(): void {
    const store = useAppStore.getState();
    // Effacer tous les styles actuels;
    const currentStyles = this.getAllStyles();
    currentStyles.forEach(style => {
      store.deleteTextStyle(style.id);
    });
    // Recr\u00e9er les styles par d\u00e9faut;
    this.initializeDefaultStyles();
    logger.info('\ud83d\udd04 Styles r\u00e9initialis\u00e9s aux valeurs par d\u00e9faut');
  }
  /**
   * Exporte tous les styles en JSON;
   */
  public exportStyles(): string {
    const styles = this.getAllStyles();
    return JSON.stringify(styles, null, 2);
  }
  /**
   * Importe des styles depuis JSON;
   */
  public importStyles(jsonString: string): void {
    try {
      const importedStyles: TextStyle[] = JSON.parse(jsonString);
      // Valider que c'est un tableau de styles;
      if (!Array.isArray(importedStyles)) {
        throw new Error('Format invalide');
      }
      const store = useAppStore.getState();
      // Ajouter chaque style import\u00e9;
      importedStyles.forEach(style => {
        // R\u00e9g\u00e9n\u00e9rer l'ID pour \u00e9viter les conflits;
        const newId = this.generateStyleId(style.name);
        store.addTextStyle({ ...style, id: newId });
      });
      this.saveToStorage(this.getAllStyles());
      logger.info(`\u2705 ${importedStyles.length} styles import\u00e9s`);
    } catch (error) {
      logger.error("Erreur lors de l'import des styles:", error);
    }
  }
  /**
   * G\u00e9n\u00e8re un ID unique pour un style;
   */
  private generateStyleId(name: string): string {
    const sanitized = name
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-|-$/g, '');
    const timestamp = Date.now();
    return `${sanitized}-${timestamp}`;
  }
  /**
   * Convertit un fontWeight en string fontStyle pour Konva;
   */
  private getFontStyleString(fontWeight: number): string {
    if (fontWeight >= 700) {
      return 'bold';
    } else if (fontWeight >= 600) {
      return '600';
    } else if (fontWeight >= 500) {
      return '500';
    }
    return 'normal';
  }
  /**
   * Convertit un string fontStyle en fontWeight;
   */
  private getFontWeightFromString(fontStyle: string): number {
    if (fontStyle.includes('bold')) {
      return 700;
    } else if (fontStyle.includes('600')) {
      return 600;
    } else if (fontStyle.includes('500')) {
      return 500;
    }
    return 400;
  }
  /**
   * Sauvegarde les styles dans localStorage;
   */
  private saveToStorage(styles: TextStyle[]): void {
    try {
      localStorage.setItem(TextStyleManager.STORAGE_KEY, JSON.stringify(styles));
    } catch (error) {
      logger.error('Erreur lors de la sauvegarde des styles:', error);
    }
  }
  /**
   * Charge les styles depuis localStorage;
   */
  private loadFromStorage(): TextStyle[] {
    try {
      const saved = localStorage.getItem(TextStyleManager.STORAGE_KEY);
      if (saved) {
        return JSON.parse(saved);
      }
      return [];
    } catch (error) {
      logger.error('Erreur lors du chargement des styles:', error);
      return [];
    }
  }
  /**
   * R\u00e9cup\u00e8re les styles par d\u00e9faut (statique);
   */
  public static getDefaultStyles(): TextStyle[] {
    return [...TextStyleManager.DEFAULT_STYLES];
  }
  /**
   * Nettoie les ressources;
   */
  public destroy(): void {
    logger.info('\ud83e\uddf9 TextStyleManager nettoy\u00e9');
  }
}
